import { Order, OrderItem, PaymentItem, Product, Voucher } from "depoto-core";
import {toast} from "react-toastify";
import {getDateStringFromUTCString} from "./strings.ts";
import {CheckoutOrderItem, CustomCheckoutOrder, initOrder} from "../store/core/reducer.ts";

type AddItemToBillArgs = {
  currentOrder: CustomCheckoutOrder | null
  setCurrentOrder?: (o: CustomCheckoutOrder) => void
  orderItem?: CheckoutOrderItem
  product?: Product
  orderItemUuidToRemove?: string
  // percentageSales: Array<Partial<Voucher>>
}
export const updateBill = ({ currentOrder, setCurrentOrder, orderItem, product, orderItemUuidToRemove }: AddItemToBillArgs) => {
  let nextOrder: CustomCheckoutOrder
  if (!currentOrder?.items) {
    nextOrder = initOrder()
  } else {
    nextOrder = {...currentOrder}
  }
  // console.log({product, orderItem, orderItemUuidToRemove, percentageSales: nextOrder.percentageSales})
  if (product) {
    if (nextOrder.items?.find(oi => oi.product?.id === product.id)) {
      for (const oi of nextOrder.items) {
        if (oi.product?.id === product.id) {
          const q = oi.quantity + 1
          oi.quantity = q
          oi.price = round2(product.sellPrice)
          oi.priceWithoutVat = product.sellPriceWithoutVat
          oi.priceAll = round2(product.sellPrice) * q
          oi.priceAllWithoutVat = product.sellPriceWithoutVat * q
          oi.ean = product.ean
          oi.code = product.code
          oi.vat = product.vat
          // todo VAT!!!!
        }
      }
      toast.success('Položka byla upravena')
    } else {
      nextOrder.items.push(new OrderItem({
        type: OrderItemType.product,
        product,
        name: product.fullName,
        quantity: 1,
        price: round2(product.sellPrice),
        priceWithoutVat: product.sellPriceWithoutVat,
        priceAll: round2(product.sellPrice),
        priceAllWithoutVat: product.sellPriceWithoutVat,
        ean: product.ean,
        code: product.code,
        vat: product.vat,
      }))
      toast.success('Položka byla přidána')
    }
  } else if (orderItem) {
    if (nextOrder.items?.find(oi => oi.uuid === orderItem.uuid)) {
      for (const oi of nextOrder.items) {
        if (oi.uuid === orderItem.uuid) {
          // one sold voucher item = one created voucher, one redeemed voucher item = one voucher
          const q = oi.type === OrderItemType.voucherToCreate || oi.type === OrderItemType.voucher ? 1 : orderItem.quantity
          oi.quantity = q
          oi.price = round2(orderItem.price || orderItem.product?.sellPrice)
          oi.priceWithoutVat = orderItem.priceWithoutVat || orderItem.product?.sellPriceWithoutVat
          oi.priceAll = round2(orderItem.price || orderItem.product?.sellPrice) * q
          oi.priceAllWithoutVat = (orderItem.priceWithoutVat || orderItem.product?.sellPriceWithoutVat) * q
          oi.note = orderItem.note
          oi.sale = orderItem.sale || 0
          oi.manualSale = orderItem.sale || 0
          oi.ean = orderItem.ean
          oi.code = orderItem.code
          oi.isForSubsequentSettlement = orderItem.isForSubsequentSettlement
          oi.expirationDate = orderItem.expirationDate
          oi.vat = orderItem.vat
        }
      }
      toast.success('Položka byla upravena')
    } else {
      // todo other types!!
      const newOrderItem: CheckoutOrderItem = new OrderItem({
        type: orderItem.type,
        product: orderItem.product,
        name: orderItem.product?.fullName || orderItem.name,
        quantity: orderItem.quantity,
        price: round2(orderItem.product?.sellPrice || orderItem.price),
        priceWithoutVat: orderItem.product?.sellPriceWithoutVat || orderItem.priceWithoutVat,
        priceAll: round2(orderItem.product?.sellPrice || orderItem.price) * orderItem.quantity,
        priceAllWithoutVat: (orderItem.product?.sellPriceWithoutVat || orderItem.priceWithoutVat) * orderItem.quantity,
        note: orderItem.note,
        sale: orderItem.sale || 0,
        ean: orderItem.ean,
        code: orderItem.code,
        isForSubsequentSettlement: orderItem.isForSubsequentSettlement,
        expirationDate: orderItem.expirationDate,
        vat: orderItem.vat,
      })
      // checkout-only fields, core OrderItem constructor drops them
      newOrderItem.voucherId = orderItem.voucherId
      newOrderItem.voucherValue = orderItem.voucherValue
      nextOrder.items.push(newOrderItem)
      switch (orderItem.type) {
        case OrderItemType.product:
        case OrderItemType.custom:
          toast.success('Položka byla přidána')
          break
        case OrderItemType.sale:
          toast.success('Sleva byla přidána')
          break
        case OrderItemType.voucher:
          toast.success('Poukaz byl uplatněn')
          break
      }
    }
  } else if (orderItemUuidToRemove?.length) {
    nextOrder.items = nextOrder.items.filter(oi => oi.uuid !== orderItemUuidToRemove)
    toast.success('Položka byla odstraněna')
  } else if (!product && !orderItem && !orderItemUuidToRemove) {
    toast.success('Objednávka byla upravena')
  }

  nextOrder.priceAllExact = round2(calculateSalesAndCoupons(nextOrder, nextOrder.percentageSales))
  nextOrder.vatAllocations = calcVatAllocations(nextOrder)
  nextOrder = applyCashRounding(nextOrder)
  // console.log({nextOrder})
  typeof setCurrentOrder === "function" ? setCurrentOrder(nextOrder) : null
  return nextOrder
}

const calculateSalesAndCoupons = (order: CustomCheckoutOrder, percentageSales?: Array<Partial<Voucher>>) => {
  const extraPercent = percentageSales?.length
    ? percentageSales
      .map(ps => ps.discountPercent || 0)
      .reduce((a, b) => a + b, 0)
    : 0

  order.items.forEach(oi => {
    // redeemed voucher is not a discount, percentage sales do not apply to it
    if (oi.type === OrderItemType.voucher) {
      oi.sale = 0
      return
    }
    const manualPercent =
      typeof oi.manualSale === 'number'
        ? oi.manualSale
        : (typeof oi.sale === 'number' ? oi.sale : 0)
    oi.manualSale = manualPercent
    const appliedPercent = Math.max(manualPercent, extraPercent)
    oi.sale = appliedPercent
  })
  applyRedeemedVoucherValues(order)

  // todo priceAllWithoutVat into core.Order
  const priceAll = order.items
    .map(oi => getDiscountedPricesForOrderItem(oi).priceAll)
    .reduce((a, b) => a + b, 0)
  return priceAll
}

// redeemed single-purpose voucher = negative item with VAT (VAT was paid when the voucher was sold),
// its value is capped by goods with the same VAT, so VAT allocation for that rate can not go below zero
const applyRedeemedVoucherValues = (order: CustomCheckoutOrder) => {
  const usedByVatPercent: { [vatPercent: number]: number } = {}
  order.items
    .filter(oi => oi.type === OrderItemType.voucher)
    .forEach(oi => {
      const vatPercent = oi.vat?.percent ?? 0
      const goodsWithSameVat = order.items
        .filter(goods => goods.type !== OrderItemType.voucher && (goods.vat?.percent ?? 0) === vatPercent)
      // use the same vat as goods (company can have more vats with the same percent, API makes VAT allocation per vat)
      if (goodsWithSameVat[0]?.vat) {
        oi.vat = goodsWithSameVat[0].vat
      }
      const goodsSum = goodsWithSameVat
        .reduce((sum, goods) => sum + getDiscountedPricesForOrderItem(goods).priceAll, 0)
      const available = Math.max(0, goodsSum - (usedByVatPercent[vatPercent] || 0))
      const value = round2(Math.min(Number(oi.voucherValue) || 0, available))
      usedByVatPercent[vatPercent] = (usedByVatPercent[vatPercent] || 0) + value
      oi.quantity = 1
      oi.price = -value
      oi.priceAll = -value
      oi.priceWithoutVat = vatPercent === 0 ? -value : -value * oi.vat.coefficient
      oi.priceAllWithoutVat = oi.priceWithoutVat
    })
}

export const getDiscountedPricesForOrderItem = (orderItem: OrderItem) => {
  // todo coupons?
  const salePercent = orderItem.sale / 100
  const discount = orderItem.price * salePercent
  const discountedPrice = orderItem.price - discount
  const vatCoefficient = orderItem.vat?.coefficient ?? 1
  return {
    price: discountedPrice,
    priceWithoutVat: discountedPrice * vatCoefficient,
    priceAll: discountedPrice * orderItem.quantity,
    priceAllWithoutVat: discountedPrice * vatCoefficient * orderItem.quantity,
  }
}

export const calcVatAllocations = (order: CustomCheckoutOrder) => {
  const uniqueVatPercents = Array.from(new Set(order.items.map(oi => oi.vat?.percent ?? 0)))
  const vatAllocationsObj = {}
  for (const uniqueVat of uniqueVatPercents) {
    vatAllocationsObj[uniqueVat] = {
      vat: uniqueVat,
      priceWithoutVat: 0,
      vatPrice: 0,
      price: 0,
    }
  }
  order.items.forEach(oi => {
    const vatPercent = oi.vat?.percent ?? 0
    vatAllocationsObj[vatPercent].priceWithoutVat += oi.priceAllWithoutVat
    vatAllocationsObj[vatPercent].price += oi.priceAll
  })
  const vatAllocations = []
  const vatSums = { vat: 'Celkem', priceWithoutVat: 0, vatPrice: 0, price: 0 }
  for (const va in vatAllocationsObj) {
    vatAllocationsObj[va].vatPrice = vatAllocationsObj[va].price - vatAllocationsObj[va].priceWithoutVat
    vatAllocations.push(vatAllocationsObj[va])
    vatSums.priceWithoutVat += vatAllocationsObj[va].priceWithoutVat
    vatSums.vatPrice += vatAllocationsObj[va].vatPrice
    vatSums.price += vatAllocationsObj[va].price
  }
  vatAllocations.push(vatSums)
  return vatAllocations
}

export const round2 = (num: number): number => Math.round(((Number(num) || 0) + Number.EPSILON) * 100) / 100

// only cash is rounded to whole crowns (no coins under 1 Kč), card and coupon payments are exact
export const isCashRoundedToWhole = (currencyId?: string): boolean => !currencyId || currencyId === 'CZK'

export const getPaymentAmountForType = (paymentTypeId: string, amount: number, currencyId?: string): number =>
  paymentTypeId === 'cash' && isCashRoundedToWhole(currencyId) ? Math.round(amount) : round2(amount)

// rounding = rounded cash part - exact cash part; priceAll includes rounding (same as on API)
export const applyCashRounding = (order: CustomCheckoutOrder, currencyId?: string): CustomCheckoutOrder => {
  const nextOrder = {...order}
  const priceAllExact = round2(nextOrder.priceAllExact ?? nextOrder.priceAll)
  nextOrder.priceAllExact = priceAllExact
  nextOrder.rounding = 0
  const hasCashPayment = nextOrder.paymentItems?.some(pi => pi?.payment?.type?.id === 'cash')
  if (hasCashPayment && isCashRoundedToWhole(currencyId)) {
    const nonCashSum = nextOrder.paymentItems
      .filter(pi => pi?.payment?.type?.id !== 'cash')
      .reduce((sum, pi) => sum + Number(pi?.amount || 0), 0)
    const exactCash = Math.max(0, priceAllExact - nonCashSum)
    nextOrder.rounding = round2(Math.round(exactCash) - exactCash)
  }
  nextOrder.priceAll = round2(priceAllExact + nextOrder.rounding)
  return nextOrder
}

export const OrderItemType = {
  product: 'product',
  custom: 'custom', // product without product -> na API uz jen jako product
  sale: 'sale',
  voucher: 'voucher',
  voucherToCreate: 'voucherToCreate',
}

export const formatPrice = (price: number, currencyId?: string) => {
  let currency: string
  switch (currencyId) {
    default:
    case 'CZK':
      currency = 'Kč'
      break
    case 'EUR':
      currency = '€'
      break
    case 'USD':
      currency = '$'
      break
  }
  return currency === 'EUR' || currency === 'USD'
    ? `${currency} ${(price || 0).toFixed(2)}`
    : `${(price || 0).toFixed(2)} ${currency}`
}

export const prepareOrderItems = (order: Partial<CustomCheckoutOrder>) => {
  const orderItems: any[] = []
  order.items?.forEach((orderItem: CheckoutOrderItem) => {
    const type = orderItem.type === OrderItemType.custom ? OrderItemType.product
      : orderItem.type === OrderItemType.voucherToCreate ? OrderItemType.product
        : orderItem.type === OrderItemType.sale ? OrderItemType.product
          : orderItem.type // rest stays as it is: shipping, used vouchers, etc
    const oi: any = {
      name: orderItem.name?.length ? orderItem.name : orderItem.product ? orderItem.product.fullName : '',
      quantity: orderItem.quantity,
      type,
      sale: Number(orderItem.sale) > 0 ? Number(orderItem.sale) : 0,
      price: Number(orderItem.price) || 0,
      vat: orderItem.vat?.id,
      isForSubsequentSettlement: orderItem.isForSubsequentSettlement,
    }
    if (orderItem.id && orderItem.id > 0) {
      oi.id = orderItem.id
    }
    if (orderItem.product && orderItem.product.id > 0) {
      oi.product = orderItem.product && orderItem.product.id > 0 ? orderItem.product.id : orderItem.id
    }
    if (orderItem.ean && orderItem.ean.length > 0) {
      oi.ean = orderItem.ean
    }
    if (orderItem.code && orderItem.code.length > 0) {
      oi.code = orderItem.code
    }
    if (orderItem.serial && orderItem.serial.length > 0) {
      oi.serial = orderItem.serial
    }
    if (orderItem.note && orderItem.note.length > 0) {
      oi.note = orderItem.note
    }
    if (orderItem.voucherId > 0) {
      oi.voucher = orderItem.voucherId
    }
    orderItems.push(oi)
  })
  return orderItems
}

export const preparePaymentItems = (order: Partial<CustomCheckoutOrder>) => {
  const paymentItems: any[] = []
  order.paymentItems?.forEach((pi: PaymentItem) => {
    const p: any = {
      payment: Number(pi.payment?.id),
      amount: pi.amount,
      dateCreated: getDateStringFromUTCString(pi.dateCreated),
      isPaid: pi.isPaid,
    }
    if (pi.checkout && pi.checkout.id > 0) {
      p.checkout = pi.checkout.id
    }
    if (pi.dateCancelled) {
      p.dateCancelled = getDateStringFromUTCString(pi.dateCancelled)
    }
    if (pi.voucher?.id) {
      p.voucher = pi.voucher.id
      console.log('voucher paymentItem: ', p)
    }
    paymentItems.push(p)
  })
  return paymentItems
}

type CloneOrderArgs = {
  orderToClone: CustomCheckoutOrder | null
  currentOrder: CustomCheckoutOrder | null
  setCurrentOrder?: (o: CustomCheckoutOrder) => void
}
export const cloneOrder = ({ orderToClone, currentOrder, setCurrentOrder }: CloneOrderArgs) => {
  if (currentOrder?.items?.length) {
    const rewriteOrder = confirm('Máte rozpracovanou objednávku. Chcete ji smazat?')
    if (!rewriteOrder) {
      return
    }
  }
  let o = initOrder()
  setCurrentOrder(o)
  orderToClone.items.forEach(async orderItem => {
    console.log(orderItem)
    o = updateBill({ currentOrder: o, setCurrentOrder, orderItem })
  })
  // const o = initOrder()
  // o.items = [...order.items].map(oi => ({ ...oi, uuid: generateUuidV4() }))
  o.paymentItems = [...orderToClone.paymentItems]
  o = applyCashRounding(o)
  if (orderToClone.customer?.id) {
    o.customer = {...orderToClone.customer}
  }
  if (orderToClone.reservationNumber > 0) {
    o.reservationNumber = orderToClone.reservationNumber
  }
  if (orderToClone.id > 0) {
    o.id = orderToClone.id
  }
  setCurrentOrder(o)
}
